import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  useCommunicationIntentsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { getLabelIdsForAction } from '../../../utils/emailLabelHelpers';
import { navigateToInboxOrDoBox } from '../../../utils/navigation';
import { useActiveInboxEntriesSlice } from '../../InboxEntries';
import { useCustomToast } from '../../useCustomToast';
import { useCancelSnoozeOnRemoval } from '../snooze';

export const useRemoveUserCommunication = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cancelSnoozeFor } = useCancelSnoozeOnRemoval();

  const {
    setDeleteLoading,
    updateUserCommunicationLabels,
    isLoading: isDeleteLoading,
    getUserCommunicationByMessageId,
  } = useUserCommunicationsStore();
  const { setLabelsIntent, clearIntent, settleIntent } =
    useCommunicationIntentsStore();
  const {
    removeUserCommunicationsFromInboxEntries,
    removeInboxEntry,
    getCommunicationsByInboxEntryId,
  } = useActiveInboxEntriesSlice();

  const { errorMessage, warningMessage } = useCustomToast();

  const handleRemoveUserCommunication = useCallback(
    async (
      messageIds: string | string[],
      options?: { shouldNavigate?: boolean },
    ) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const communicationsToDelete = messageIdsArray
        .map((messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);
          if (!userCommunication) {
            log.error(
              `UserCommunication with messageId ${messageId} not found`,
            );
            return null;
          }
          return { messageId, communication: userCommunication };
        })
        .filter(
          (
            item,
          ): item is { messageId: string; communication: UserCommunication } =>
            item !== null,
        )
        .filter(
          ({ communication }) =>
            !communication.labelIds?.includes(LabelId.TRASH),
        );

      if (communicationsToDelete.length === 0) {
        warningMessage(t('inbox.userCommunication.actions.alreadyInTrash'));
        return;
      }

      const linkedEntryIds = Array.from(
        new Set(
          communicationsToDelete.flatMap(
            ({ communication }) => communication.inboxEntryIds ?? [],
          ),
        ),
      );

      const entryIdsToRemove = linkedEntryIds.filter((entryId) => {
        const currentCommunications = getCommunicationsByInboxEntryId(entryId);
        const remainingCommunications = (currentCommunications || []).filter(
          (comm) =>
            !communicationsToDelete.some((c) => c.messageId === comm.messageId),
        );
        return remainingCommunications.length === 0;
      });

      setDeleteLoading(true);

      const labelBackupMap = new Map<string, LabelId[]>();

      communicationsToDelete.forEach(({ messageId, communication }) => {
        labelBackupMap.set(messageId, communication.labelIds || []);
        const expectedLabelIds = getLabelIdsForAction(
          communication.labelIds,
          'trash',
        );

        setLabelsIntent({
          messageId,
          threadMessageId: communication.threadMessageId,
          internetMessageId: communication.internetMessageId,
          originalLabelIds: communication.labelIds,
          expectedLabelIds,
        });
        updateUserCommunicationLabels(messageId, expectedLabelIds);
      });

      const revertOptimisticTrash = (messageIdsToRevert: string[]) => {
        messageIdsToRevert.forEach((messageId) => {
          const previousLabels = labelBackupMap.get(messageId);
          if (previousLabels) {
            updateUserCommunicationLabels(messageId, previousLabels);
          }
          clearIntent(messageId);
        });
      };

      try {
        const operations = communicationsToDelete.map(
          async ({ messageId, communication }) => {
            try {
              if (!communication.userEmailAddress) {
                throw new Error('No user email address found in communication');
              }

              if (!communication.platform) {
                throw new Error('No platform found in communication');
              }

              await modifyEmail({
                userEmailAddress: communication.userEmailAddress,
                platform: communication.platform,
                messageId: communication.messageId,
                action: EmailRequestType.TRASH,
              });

              settleIntent(messageId);

              return { messageId, success: true, communication };
            } catch (error) {
              log.error(
                `Failed to trash email for messageId ${messageId}:`,
                error,
              );
              return { messageId, success: false, error, communication };
            }
          },
        );

        const results = await Promise.all(operations);
        const failed = results.filter((result) => !result.success);

        if (failed.length === 0) {
          log.info(
            `${communicationsToDelete.length} messages deleted successfully`,
          );

          removeUserCommunicationsFromInboxEntries(
            communicationsToDelete.map((comm) => comm.messageId),
          );

          cancelSnoozeFor(
            communicationsToDelete.map(({ messageId }) => messageId),
          );

          if (entryIdsToRemove.length > 0) {
            await Promise.all(
              entryIdsToRemove.map(async (entryId) => {
                await removeInboxEntry(entryId);
                log.info(
                  `Removed inbox entry ${entryId} as last message was deleted`,
                );
              }),
            );
          }

          if (options?.shouldNavigate) {
            const currentTab = searchParams.get('tab');
            navigateToInboxOrDoBox(navigate, currentTab);
          }
        } else {
          log.warn(
            `${failed.length} operations failed out of ${communicationsToDelete.length}`,
          );

          revertOptimisticTrash(failed.map((result) => result.messageId));

          errorMessage(
            failed.length === communicationsToDelete.length
              ? t('inbox.userCommunication.actions.deleteError')
              : t('inbox.userCommunication.actions.partialDeleteError', {
                  failed: failed.length,
                  total: communicationsToDelete.length,
                }),
          );
        }
      } catch (error) {
        log.error(error);
        revertOptimisticTrash(messageIdsArray);
        errorMessage(t('inbox.userCommunication.actions.deleteError'));
      } finally {
        setDeleteLoading(false);
      }
    },
    [
      updateUserCommunicationLabels,
      setDeleteLoading,
      errorMessage,
      warningMessage,
      navigate,
      searchParams,
      t,
      getUserCommunicationByMessageId,
      getCommunicationsByInboxEntryId,
      removeUserCommunicationsFromInboxEntries,
      removeInboxEntry,
      setLabelsIntent,
      clearIntent,
      settleIntent,
      cancelSnoozeFor,
    ],
  );

  return {
    isDeleteLoading,
    handleRemoveUserCommunication,
  };
};
